import { Reducer } from 'redux'
import storage from 'redux-persist/lib/storage'
import expireReducer from 'redux-persist-expire'
import { autoMergeDeep, getPersistConfig } from 'redux-deep-persist'
import { PersistConfig, persistReducer } from 'redux-persist'
import hardSetReconciler from 'redux-persist/lib/stateReconciler/hardSet'

import { userReducer } from '../user/userSlice'
import { featuresReducer } from '../../store/features'
import { sameBotIdOnly } from './sameBotIdReconciler'
import { UndoableAppState } from '../../reducers/app.types'
import { dockReducer, DockSliceState } from '../dock'
import { dialogStepsReducer } from '../dialogSteps'
import { reducer as commentReducer } from '../comment/commentsSlice'
import { DeployBotState } from '../deployBot/types'
import { deployBotReducer } from '../deployBot'
import {
  initialState as visitStatisticsInitialState,
  visitStatisticsReducer,
  VisitStatisticsSlice
} from '../visitStatistics/visitStatisticsSlice'
import { serverApi } from '../serverApi'
import { getLoadedBotIdFromUrl } from '../../lib/urlService'
import { nlpEditorReducer } from '../nlpEditor'
import { FEATURES_PERSIST_KEY, SERVER_API_PERSIST_KEY } from './constants'

export const appPersistor = (reducer: Reducer<UndoableAppState>) => {
  const config = getPersistConfig({
    key: `app-${getLoadedBotIdFromUrl()}`,
    storage,
    blacklist: ['present.updateHostingLoading'],
    rootReducer: reducer
  })

  config.stateReconciler = sameBotIdOnly(autoMergeDeep)

  return persistReducer(config, reducer)
}

export const dockPersistor = (reducer: typeof dockReducer) => {
  const config = getPersistConfig({
    key: 'dock-3',
    storage,
    rootReducer: dockReducer,
    blacklist: ['sections.try.isRunning', 'sections.try.webchatLog', 'sections.try.introDialogId']
  })
  config.stateReconciler = sameBotIdOnly(autoMergeDeep)

  return persistReducer(config, reducer as Reducer<DockSliceState>)
}

export const deployBotPersistor = (reducer: typeof deployBotReducer) => {
  const config: PersistConfig<any> = {
    key: 'deployBot',
    storage,
    whitelist: ['botIdsForPossibleResumeHosting']
  }

  return persistReducer(config, reducer as Reducer<DeployBotState, any>)
}

export const featuresPersistor = (reducer: typeof featuresReducer) => {
  const config: PersistConfig<any> = {
    key: FEATURES_PERSIST_KEY,
    storage,
    blacklist: ['isStatsigApiReady']
  }

  return persistReducer(config, reducer)
}

export const dialogStepsPersistor = (reducer: typeof dialogStepsReducer) => {
  const config: PersistConfig<any> = {
    key: 'dialogSteps',
    storage,
    stateReconciler: sameBotIdOnly(hardSetReconciler)
  }
  return persistReducer(config, reducer)
}

export const userPersistor = (reducer: typeof userReducer) => {
  const expireTransform = expireReducer('info', {
    expireSeconds: 60 * 60,
    autoExpire: true
  })

  const config: PersistConfig<any> = {
    key: 'user',
    storage,
    whitelist: ['info'],
    transforms: [expireTransform]
  }

  return persistReducer(config, reducer)
}

export const commentPersistor = (reducer: typeof commentReducer) => {
  const config: PersistConfig<any> = {
    key: `comments-${getLoadedBotIdFromUrl()}`,
    storage
  }
  return persistReducer(config, reducer)
}

export const visitStatisticsPersistor = (reducer: typeof visitStatisticsReducer) => {
  const whitelist: Array<keyof VisitStatisticsSlice> = ['showVisitStatistics', 'dateFrom', 'dateTo']

  const config: PersistConfig<any> = {
    key: 'visitStatistix',
    whitelist,
    storage,
    stateReconciler: (...params) => {
      return {
        ...visitStatisticsInitialState,
        ...sameBotIdOnly(hardSetReconciler)(...params)
      }
    }
  }

  return persistReducer(config, reducer)
}

export const serverApiPersistor = (reducer: typeof serverApi.reducer) => {
  const config: PersistConfig<any> = {
    key: SERVER_API_PERSIST_KEY,
    storage,
    // Do not restore - state is rehydrated inside onQueryStarted
    stateReconciler: (persisted, defaultState) => defaultState
  }

  return persistReducer(config, reducer)
}

export const nlpEditorPersistor = (reducer: typeof nlpEditorReducer) => {
  const config: PersistConfig<any> = {
    key: `nlpEditor-${getLoadedBotIdFromUrl()}`,
    storage
  }
  return persistReducer(config, reducer)
}
